import { Calendar, Globe, House, SquareCheckBig, User, Users } from "lucide-react";
import { AskCategory, AskScope, DealPhase, DealType, Financing } from "@/enum/common.enum";
import type { CountryCode } from "libphonenumber-js/max";

export const COLLAB_ROLE_HEADLINES: Record<string, string> = {
  lender: "Your lending lane.",
  attorney: "Your closing checklist.",
  vendor: "Your work order.",
  co_agent: "The shared deal room.",
};

export const DEAL_SIDE_LABELS = { sell: "seller side", buy: "buyer side", neutral: "shared" } as const;

export const RESOLVED_ROLE_STYLES = {
  buyer_client: { bg: "bg-bone", text: "text-ink border border-edge", label: "Buyer" },
  seller_client: { bg: "bg-bone", text: "text-ink border border-edge", label: "Seller" },
  buyer_agent: { bg: "bg-moss", text: "text-linen", label: "Buyer's agent" },
  listing_agent: { bg: "bg-moss", text: "text-linen", label: "Listing agent" },
  co_agent: { bg: "bg-mossDark", text: "text-linen", label: "Co-agent" },
  transaction_coordinator: { bg: "bg-mossDark", text: "text-linen", label: "TC" },
  lender: { bg: "bg-slate1", text: "text-linen", label: "Lender" },
  closing_attorney: { bg: "bg-rust", text: "text-linen", label: "Closing attorney" },
  vendor: { bg: "bg-stone", text: "text-linen", label: "Vendor" },
  agent: { bg: "bg-moss", text: "text-linen", label: "Agent" },
  client: { bg: "bg-bone", text: "text-ink border border-edge", label: "Client" },
  attorney: { bg: "bg-rust", text: "text-linen", label: "Attorney" },
  coordinator: { bg: "bg-mossDark", text: "text-linen", label: "Coordinator" },
} as const;

export const ROLE_STYLES = {
  agent: RESOLVED_ROLE_STYLES.agent,
  co_agent: RESOLVED_ROLE_STYLES.co_agent,
  lender: RESOLVED_ROLE_STYLES.lender,
  attorney: RESOLVED_ROLE_STYLES.attorney,
  client: RESOLVED_ROLE_STYLES.client,
  vendor: RESOLVED_ROLE_STYLES.vendor,
} as const;

export const ROLE_TAG_HEX: Record<string, string> = {
  buyer_client: "var(--dash-amber)",
  seller_client: "var(--dash-amber)",
  buyer_agent: "var(--dash-green-mid)",
  listing_agent: "var(--dash-green-mid)",
  co_agent: "var(--dash-green-mid)",
  transaction_coordinator: "var(--dash-green-mid)",
  lender: "var(--dash-slate)",
  closing_attorney: "var(--dash-rust)",
  vendor: "var(--dash-muted)",
  agent: "var(--dash-green-mid)",
  client: "var(--dash-amber)",
  attorney: "var(--dash-rust)",
  coordinator: "var(--dash-green-mid)",
};

export const DEFAULT_ROLE_TAG_HEX = "var(--dash-muted)";

export const CLIENT_TASK_STATUS_PILLS: Record<string, { label: string; cls: string }> = {
  complete: { label: "DONE", cls: "bg-dash-greenMid/10 text-dash-greenMid" },
  in_progress: { label: "IN PROGRESS", cls: "bg-dash-amber/10 text-dash-amber" },
  pending: { label: "TO DO", cls: "bg-stone/10 text-stone" },
};

// Client item 14: the Deal Status reads off the checklist in three states —
// "At risk" (a task is overdue), "On track" (everything within its timeline)
// and "Ahead of schedule" (tasks finished before they were due). `watching`
// is no longer computed; it stays only so an older manual pin still renders.
export const DEAL_SNAPSHOT_HEALTH = {
  ahead: { label: "Ahead of schedule", chipBg: "bg-dash-greenMid/10 text-dash-greenMid border-dash-greenMid/30", dot: "bg-dash-greenMid" },
  on_track: { label: "On track", chipBg: "bg-dash-greenMid/10 text-dash-greenMid border-dash-greenMid/30", dot: "bg-dash-greenMid" },
  watching: { label: "On track", chipBg: "bg-dash-greenMid/10 text-dash-greenMid border-dash-greenMid/30", dot: "bg-dash-greenMid" },
  at_risk: { label: "At risk", chipBg: "bg-dash-amber/10 text-dash-amber border-dash-amber/30", dot: "bg-dash-amber" },
} as const;

export const MILESTONE_TOOLTIPS = {
  uc: "The date the contract was signed and became binding for everyone.",
  anchor: "The date the contract was signed and became binding for everyone.",
  event: "A scheduled event on this deal. If a note was added, you'll see it below.",
  dd: "Due diligence ends here — this is the last day to negotiate repairs or back out based on inspection findings without losing your earnest money.",
  close: "Closing day — you sign the paperwork and the sale finalizes. Buyers get the keys; sellers hand them over.",
  financing: "Financing contingency expires here — the last day the buyer can walk away over loan approval without losing their earnest money.",
} as const;

export const DEAL_HERO_URGENCY = {
  calm: { border: "border-edge", wash: "bg-linen/30", overline: "text-stone", value: "text-ink", unit: "text-stone", ring: "stroke-moss", badge: null, badgeCls: "" },
  warning: { border: "border-dash-amber/30", wash: "bg-dash-amber/10", overline: "text-dash-amber", value: "text-ink", unit: "text-dash-amber", ring: "stroke-dash-amber", badge: "Approaching", badgeCls: "border-dash-amber/40 text-dash-amber" },
  urgent: { border: "border-rust/50", wash: "bg-rust/5", overline: "text-rust", value: "text-rust", unit: "text-rust", ring: "stroke-rust", badge: "Urgent", badgeCls: "border-rust/50 text-rust" },
  overdue: { border: "border-rust", wash: "bg-rust/10", overline: "text-rust", value: "text-rust", unit: "text-rust", ring: "stroke-rust", badge: "Overdue", badgeCls: "border-rust text-rust" },
  done: { border: "border-dash-greenMid/30", wash: "bg-dash-greenMid/10", overline: "text-dash-greenMid", value: "text-dash-greenMid", unit: "text-dash-greenMid", ring: "stroke-dash-greenMid", badge: "Complete", badgeCls: "border-dash-greenMid/40 text-dash-greenMid" },
} as const;

export const DEAL_TABS = [
  { k: "overview", l: "Overview" },
  { k: "timeline", l: "Tasks & Timeline" },
  { k: "schedule", l: "Showings & Meetings" },
  { k: "documents", l: "Documents" },
  { k: "team", l: "People & Access" },
  { k: "chat", l: "Chat" },
  { k: "voice", l: "Communication" },
  { k: "activity", l: "Activity" },
  { k: "ai", l: "AI Studio" },
  { k: "share_link", l: "Share" },
] as const;

export const AUDIENCE_LABELS: Record<string, string> = {
  internal: "My team only",
  externals: "Team + lender, attorney, other agent",
  client: "Everyone, including the client",
};

export const CLIENT_RELEASE_LABELS: Record<string, string> = {
  auto: "As soon as it is added",
  after_confirmed: "Once it is confirmed to have happened",
  on_approval: "Only when I send it",
};

export const REQUEST_TYPE_OPTIONS = [
  { value: "none", label: "Nothing — just a task" },
  { value: "action", label: "Take an action" },
  { value: "signature", label: "Sign a document" },
  { value: "document", label: "Send a document" },
  { value: "info", label: "Share information" },
];

export const DEAL_COMMUNICATION_STATUS = {
  open: { label: "Awaiting response", tone: "warning" },
  answered: { label: "Responded", tone: "info" },
  closed: { label: "Resolved", tone: "success" },
} as const;

export const TASK_PRIORITY_STYLES: Record<string, string> = {
  high: "bg-[#a65c4a1a] text-dash-rust",
  medium: "bg-[#b779341a] text-dash-amber",
  low: "bg-dash-chip text-dash-muted",
};

export const TASK_STATUS_STYLES: Record<string, string> = {
  overdue: "bg-[#a65c4a1a] text-dash-rust",
  in_progress: "bg-[#536c7a1a] text-dash-slate",
  complete: "bg-[#3e76551a] text-dash-greenMid",
  waiting: "bg-[#b779341a] text-dash-amber",
  pending: "bg-dash-chip text-dash-muted",
};

export const TASK_STATUS_LABELS: Record<string, string> = {
  overdue: "Overdue",
  in_progress: "In progress",
  complete: "Completed",
  waiting: "Waiting",
  pending: "Not started",
};

export const TASK_BADGE_CLASS_NAME = "rounded-full border-transparent px-2 py-0.5 text-[11px] font-medium shadow-none hover:brightness-95";

// Transaction leads. A buyer deal may have no property yet, so the address
// rule cannot be evaluated until the side is known — asking for the address
// first is what forced every house-hunting deal to invent one.
export const NEW_DEAL_STEPS = [
  { title: "Transaction", description: "Representation & client", icon: Calendar },
  { title: "Property", description: "Address & price", icon: House },
  { title: "Review", description: "Confirm & create", icon: SquareCheckBig },
] as const;

export const NEW_DEAL_STEP_HINTS = [
  "Start with who you represent.",
  "Buyer-side deals can skip the address until you have a property.",
  "One final look before you create.",
] as const;

export type NewDealDraft = {
  address: string;
  country: string;
  city: string;
  state: string;
  zip_code: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  client_phone_country: CountryCode;
  co_agent_name: string;
  co_agent_email: string;
  co_agent_phone: string;
  co_agent_phone_country: CountryCode;
  additional_clients: Array<{
    name: string;
    email: string;
    phone: string;
    phone_country: CountryCode;
  }>;
  under_contract: boolean;
  deal_type: DealType;
  financing: Financing;
  phase: DealPhase;
  list_price: string;
  close_date: string;
  hero_image_url: string;
};

export const NEW_DEAL_EMPTY_FORM: NewDealDraft = {
  address: "",
  country: "US",
  city: "",
  state: "",
  zip_code: "",
  client_name: "",
  client_email: "",
  client_phone: "",
  client_phone_country: "US",
  co_agent_name: "",
  co_agent_email: "",
  co_agent_phone: "",
  co_agent_phone_country: "US",
  additional_clients: [],
  under_contract: false,
  deal_type: DealType.SELL,
  financing: Financing.FINANCED,
  phase: DealPhase.PRE_LISTING,
  list_price: "",
  close_date: "",
  hero_image_url: "",
};

export const NEW_DEAL_INPUT_CLASS = "h-10 rounded-xl text-base sm:text-sm";

export const STATUS_STYLE = {
  open: "bg-bone text-stone",
  answered: "bg-moss text-linen",
  closed: "bg-stone text-linen",
} as const;

export const ASK_CATEGORIES = [
  { value: AskCategory.GENERAL, label: "General question", hint: "Not sure where else to file this — start here." },
  { value: AskCategory.FINANCIAL, label: "Financial / money", hint: "Anything about price, earnest money, closing costs, credits." },
  { value: AskCategory.TIMELINE, label: "Timeline / dates", hint: "Concerns about closing, DD, appraisal, or key dates." },
  { value: AskCategory.INSPECTION, label: "Inspection / repairs", hint: "Findings, negotiation, follow-ups on repairs." },
  { value: AskCategory.CLOSING, label: "Closing / day-of", hint: "Where to go, what to bring, wiring instructions." },
  { value: AskCategory.OTHER, label: "Other", hint: "None of the above quite fits." },
] as const;

export const ASK_SCOPES = [
  { value: AskScope.AGENT_ONLY, label: "Just my agent", hint: "Only your agent sees this. Best for private questions.", Icon: User },
  { value: AskScope.BOTH_AGENTS, label: "My agent + the other agent", hint: "Loop in both agents. Best for coordination.", Icon: Users },
  { value: AskScope.DEAL_WIDE, label: "Everyone on the deal", hint: "Both agents and the other client. Best for group confirmations.", Icon: Globe },
] as const;

// The same three scopes, read from the agent's seat instead of the client's.
// Their own client is always included — an update with nobody to receive it
// isn't an update — so these describe who else gets looped in.
export const DEAL_UPDATE_SCOPES = [
  { value: AskScope.AGENT_ONLY, label: "Just my client", hint: "Only your client sees this. The default.", Icon: User },
  { value: AskScope.BOTH_AGENTS, label: "My client + the other agent", hint: "Loop in the opposing agent. Best for coordination.", Icon: Users },
  { value: AskScope.DEAL_WIDE, label: "Everyone on the deal", hint: "Both agents and both clients. Best for group confirmations.", Icon: Globe },
] as const;

export const ASK_SCOPE_LABELS = {
  agent_only: "Just their agent",
  both_agents: "Both agents",
  deal_wide: "Everyone",
} as const;

export const SCOPE_STYLE: Record<string, string> = {
  agent_only: "bg-bone text-stone border-edge",
  both_agents: "bg-moss/10 text-moss border-moss/30",
  deal_wide: "bg-slate1/10 text-slate1 border-slate1/30",
};

export const CATEGORY_STYLE: Record<string, string> = {
  general: "bg-bone text-stone border-edge",
  financial: "bg-moss/10 text-moss border-moss/30",
  timeline: "bg-dash-amber/10 text-dash-amber border-dash-amber/20",
  inspection: "bg-slate1/10 text-slate1 border-slate1/30",
  closing: "bg-rust/10 text-rust border-rust/30",
  other: "bg-stone/10 text-stone border-edge",
};

export const SCOPE_SUBMIT_LABEL: Record<string, string> = {
  agent_only: "Send to my agent",
  both_agents: "Send to both agents",
  deal_wide: "Send to everyone on the deal",
};

export const findAskCategory = (value: string) => ASK_CATEGORIES.find((category) => category.value === value) || ASK_CATEGORIES[0];
export const findAskScope = (value: string) => ASK_SCOPES.find((scope) => scope.value === value) || ASK_SCOPES[0];

export const PROGRESS_BY_PHASE: Record<string, number> = {
  pre_listing: 8, pre_approval: 8, listing: 38, house_shopping: 38,
  under_contract: 76, closed: 100,
};

// Brief 1.13/1.14: the "At risk" and "Blocked" labels are suppressed — a card
// carries a plain-language signal sentence ("Earnest money 3 days overdue")
// instead. `deal_health` stays as an internal sort key (see overview.py sort
// weights), so all four keys keep an entry here; the two loud ones just read as
// the calm "Needs attention" and drop the alarm colours.
export const DEAL_HEALTH: Record<string, { label: string; className: string }> = {
  on_track: { label: "On track", className: "border-transparent bg-dash-greenMid/10 text-dash-greenMid" },
  ahead: { label: "Ahead of schedule", className: "border-transparent bg-dash-greenMid/10 text-dash-greenMid" },
  watching: { label: "Attention", className: "border-transparent bg-dash-slate/10 text-dash-slate" },
  at_risk: { label: "Attention", className: "border-transparent bg-dash-slate/10 text-dash-slate" },
  blocked: { label: "Attention", className: "border-transparent bg-dash-slate/10 text-dash-slate" },
};

export const LOAN_LABELS: Record<string, string> = {
  conventional: "Conventional", fha: "FHA", va: "VA", jumbo: "Jumbo",
  usda: "USDA", other: "Other",
};

export const PHASE_LABELS: Record<string, string> = {
  pre_listing: "Pre-listing", listing: "Listing", pre_approval: "Pre-approval",
  house_shopping: "House shopping", under_contract: "Under contract", closed: "Closed",
};

export const PHASE_LABELS_CLIENT_FOR = {
  sell: { pre_listing: "Getting ready", listing: "On the market", under_contract: "Under contract", closed: "Closed" },
  buy: { pre_approval: "Pre-approval", house_shopping: "House shopping", under_contract: "Under contract", closed: "Closed" },
} as const;

export const PHASES_FOR = {
  sell: ["pre_listing", "listing", "under_contract", "closed"],
  buy: ["pre_approval", "house_shopping", "under_contract", "closed"],
} as const;

export const INITIAL_PHASES_FOR = {
  sell: ["pre_listing", "listing", "under_contract"],
  buy: ["pre_approval", "house_shopping", "under_contract"],
} as const;

/**
 * The Deals board, left to right. One column per phase group — no column is
 * reachable by two different phases, which is what lets a card be *dragged*
 * between columns: the drop target resolves to exactly one phase to PATCH
 * (see `deals.utils.ts::phaseForColumn`).
 *
 * `phases` is ordered [sell, buy] so it can be indexed by the deal's pathway.
 * Must stay in step with `backend/analytics.py::BOARD_COLUMNS` and the
 * `board_column` $switch beside it, which is what actually tags each row.
 */
export const BOARD_COLUMNS = [
  { id: "opportunities", label: "Opportunities", phases: ["pre_listing", "pre_approval"] },
  { id: "active", label: "Active", phases: ["listing", "house_shopping"] },
  { id: "under_contract", label: "Under contract", phases: ["under_contract"] },
  { id: "closed", label: "Closed", phases: ["closed"] },
] as const;

export const RESOLVED_ROLE_LABELS: Record<string, string> = {
  buyer_client: "Buyer", seller_client: "Seller", buyer_agent: "Buyer's agent",
  listing_agent: "Listing agent", co_agent: "Co-agent",
  transaction_coordinator: "Transaction coordinator", lender: "Lender",
  closing_attorney: "Closing attorney", vendor: "Vendor", agent: "Agent",
};

// The seller's under-contract checklist deliberately leaves position 5 empty for
// the agent's own contingency rows (see backend config.py). "Add contingency"
// anchors to the row above it, so the new task lands in that gap rather than at
// the end of the list. Must stay in sync with CONTINGENCY_ANCHOR_TITLE there.
export const CONTINGENCY_ANCHOR_TITLE = "We've cleared the due diligence period";

export const APPOINTMENT_KIND_LABELS = { showing: "Showing", meeting: "Meeting", open_house: "Open House", broker_open: "Broker Open" } as const;
